import moment from 'moment-timezone';
import { DATE_INPUT_FORMAT } from './input-formats';
import { AvailableSchedulePeriod } from '../models/schedule-calendar.models';

export type ScheduleHours = Record<'available' | 'preferred', number>;

const toDate = ({
  time,
  timezoneName,
}: Partial<{ time: number; timezoneName: string }>) => {
  const zoned = timezoneName ? moment(time).tz(timezoneName) : moment(time);

  return zoned.format(DATE_INPUT_FORMAT);
};

export const calculateScheduleRange = (
  periods: AvailableSchedulePeriod[],
  timezoneName?: string
) => {
  if (!periods.length) {
    return;
  }

  const starts = periods.map(({ start }) => Date.parse(start));
  const ends = periods.map(({ end }) => Date.parse(end));

  return {
    start: toDate({
      time: Math.min(...starts),
      timezoneName,
    }),
    // A period ending at midnight ends on the day before
    end: toDate({
      time: Math.max(...ends) - 1,
      timezoneName,
    }),
  };
};

export const calculateScheduleHours = (periods: AvailableSchedulePeriod[]) =>
  periods.reduce<ScheduleHours>(
    (acc, period) => {
      const start = moment(period.start);
      const end = moment(period.end);

      const diff = end.diff(start, 'minutes') / 60;

      return {
        ...acc,
        available: acc.available + diff,
        ...(period.isPreferred && {
          preferred: acc.preferred + diff,
        }),
      };
    },
    { available: 0, preferred: 0 }
  );

export const formatScheduleHours = (hours: ScheduleHours, range?: string) =>
  [
    ...(range ? [range] : []),
    ...Object.entries(hours).map(
      ([label, hours]) => `${Number(hours.toFixed(2))} h ${label}`
    ),
  ].join(' · ');
